import React from "react";
import { AlertTriangle, HelpCircle, Lightbulb, FileText, XCircle } from "lucide-react";
import { ReadinessBadge, List } from "../ui";

export default function AssessmentPanel({ a }) {
  if (!a) return null;
  return (
    <div className="space-y-4" data-testid="assessment-panel">
      <div className="flex flex-wrap items-center gap-3">
        <ReadinessBadge value={a.readiness} />
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <div className="w-28 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#0072CE]" style={{ width: `${a.completeness}%` }} /></div>
          <span data-testid="assessment-completeness">{a.completeness}% complete</span>
        </div>
        <span className="text-[10px] text-slate-400 ml-auto">{a.engine}</span>
      </div>
      {a.readiness_reason && <p className="text-sm text-slate-700 leading-relaxed" data-testid="assessment-reason">{a.readiness_reason}</p>}
      {a.missing?.length > 0 && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 p-3" data-testid="assessment-missing">
          <div className="text-xs font-bold text-rose-800 flex items-center gap-1.5 mb-1"><XCircle size={13} /> Missing required inputs</div>
          {a.missing.map((m) => <div key={m.field} className="text-xs text-rose-900"><b>{m.label}</b> — {m.why}</div>)}
        </div>
      )}
      {a.contradictions?.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="assessment-contradictions">
          <div className="text-xs font-bold text-amber-800 flex items-center gap-1.5 mb-1"><AlertTriangle size={13} /> Contradictions & risks</div>
          {a.contradictions.map((c, i) => <div key={i} className="text-xs text-amber-900">• {c.issue}</div>)}
        </div>
      )}
      {a.follow_up_questions?.length > 0 && (
        <div data-testid="assessment-followups">
          <div className="text-xs font-bold text-slate-700 flex items-center gap-1.5 mb-1"><HelpCircle size={13} /> Follow-up questions</div>
          {a.follow_up_questions.map((q, i) => <div key={i} className="text-xs text-slate-700 mb-1"><b>{q.question}</b> <span className="text-slate-500">— {q.why}</span></div>)}
        </div>
      )}
      {a.category_advice?.length > 0 && (
        <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3" data-testid="assessment-advice">
          <div className="text-xs font-bold text-indigo-800 flex items-center gap-1.5 mb-1"><Lightbulb size={13} /> Category expert advice</div>
          <List items={a.category_advice} tone="indigo" />
        </div>
      )}
      {a.brief && (
        <details className="rounded-lg border border-slate-200 p-3" data-testid="assessment-brief">
          <summary className="text-xs font-bold text-slate-700 cursor-pointer flex items-center gap-1.5"><FileText size={13} /> Draft sanitised supplier brief</summary>
          <BriefView brief={a.brief} />
        </details>
      )}
    </div>
  );
}

export function BriefView({ brief }) {
  const rows = [["Objective", brief.objective], ["Scope", brief.scope_summary], ["Timeline", brief.timeline]];
  const lists = [["Deliverables", brief.deliverables], ["Quantities", brief.quantities], ["Markets", brief.markets], ["Languages", brief.languages], ["Constraints", brief.constraints], ["Assumptions", brief.assumptions], ["Open points", brief.open_points]];
  return (
    <div className="mt-3 space-y-3 text-sm" data-testid="brief-view">
      <div className="font-display font-semibold text-slate-900">{brief.title}</div>
      {rows.map(([k, v]) => v && <div key={k}><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{k}</div><div className="text-slate-700">{v}</div></div>)}
      <div className="grid md:grid-cols-2 gap-3">
        {lists.map(([k, v]) => v?.length > 0 && <div key={k}><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{k}</div><List items={v} /></div>)}
      </div>
    </div>
  );
}
